import { RISK_BAND_CEILINGS_PCT } from "@/domain/quant/risk-bands";
import { assetClassNameByOrder } from "@/domain/quant/portfolio-mapping";
import { parseFiniteNumber } from "@/domain/quant/number-parsing";
import { classTagsOf } from "@/domain/quant/class-mix";
import type {
  OptimizeResponse,
  ProspectItem,
  ProspectPortfolio,
  TransactionSetResponse,
} from "./types";

export interface RawV2ProspectItem {
  quant_asset_id?: string | number | null;
  asset_quant_name?: string | null;
  quantity?: string | number | null;
  value?: string | number | null;
  weight?: string | number | null;
  asset_classes?: Array<{
    name?: string | null;
    quant_asset_class_order_id?: string | number | null;
    liquidity?: boolean | null;
  }> | null;
  asset_id?: number | null;
  isin?: string | null;
  asset_name?: string | null;
  asset_class_name?: string | null;
  financial_asset_category?: string | null;
  value_sek?: string | number | null;
  fee?: string | number | null;
}

interface RawV2Prospect {
  id: string | number;
  mean_return_rate?: string | number | null;
  volatility?: string | number | null;
  cvar_rate?: string | number | null;
  prospect_portfolio_items?: RawV2ProspectItem[] | null;
  mean_return?: string | number | null;
  cvar?: string | number | null;
  variance?: string | number | null;
  fee?: string | number | null;
  value?: string | number | null;
  value_sek?: string | number | null;
  items?: RawV2ProspectItem[] | null;
}

export interface RawV2OptimizeResponse {
  id: number | string | null;
  start_value: string | number | null;
  created_at?: string | null;
  prospect_portfolios: RawV2Prospect[] | null;
}

export function mapOptimizeResponse(raw: RawV2OptimizeResponse): OptimizeResponse {
  return {
    id: raw.id ?? null,
    prospect_portfolios: (raw.prospect_portfolios ?? []).map(mapProspect),
    start_value: parseFiniteNumber(raw.start_value),
    computedAt: typeof raw.created_at === "string" ? raw.created_at : null,
  };
}

function mapProspect(raw: RawV2Prospect): ProspectPortfolio {
  const items = raw.prospect_portfolio_items ?? raw.items ?? [];
  return {
    id: numberOrFallback(raw.id, -1),
    mean_return: numberOrFallback(raw.mean_return_rate ?? raw.mean_return),
    volatility: numberOrFallback(raw.volatility),
    cvar: parseFiniteNumber(raw.cvar_rate ?? raw.cvar),
    variance: parseFiniteNumber(raw.variance),
    total_fee: parseFiniteNumber(raw.fee),
    total_value: parseFiniteNumber(raw.value_sek),
    items: items.map(mapProspectItem),
  };
}

export function mapProspectItem(raw: RawV2ProspectItem): ProspectItem {
  const assetId = parseFiniteNumber(raw.quant_asset_id) ?? parseFiniteNumber(raw.asset_id);
  return {
    isin: raw.isin ?? null,
    asset_name: raw.asset_quant_name ?? raw.asset_name ?? null,
    asset_id: assetId,
    weight: numberOrFallback(raw.weight),
    value: parseFiniteNumber(raw.value_sek) ?? numberOrFallback(raw.value),
    fee: parseFiniteNumber(raw.fee),
    asset_class_name:
      assetClassNameByOrder({ asset_classes: raw.asset_classes }, "2") ??
      raw.asset_class_name ??
      null,
    financial_asset_category:
      assetClassNameByOrder({ asset_classes: raw.asset_classes }, "1") ??
      raw.financial_asset_category ??
      null,
    is_liquidity: (raw.asset_classes ?? []).some((assetClass) => assetClass?.liquidity === true),
    classes: classTagsOf(raw.asset_classes),
  };
}

export function valueBeforeFlows(
  startValue: number | null,
  flows: { depositAmount: number; withdrawalAmount: number },
): number | null {
  if (startValue === null) return null;
  return startValue - flows.depositAmount + flows.withdrawalAmount;
}

export function applyAmountBasis(points: ProspectPortfolio[], basis: number): ProspectPortfolio[] {
  if (!Number.isFinite(basis) || basis <= 0) return points;
  return points.map((portfolio) => ({
    ...portfolio,
    total_value: basis,
    items: portfolio.items.map((item) => ({
      ...item,
      value: item.weight * basis,
    })),
  }));
}

interface RawTransaction {
  transaction_type?: string | null;
  quant_asset_id?: string | number | null;
  quantity?: string | number | null;
  amount?: string | number | null;
  amount_sek?: string | number | null;
  sell_all?: boolean | null;
  quantified_at?: string | null;
}

export interface RawTransactionSet {
  id?: string | number | null;
  quant_portfolio_id?: string | number | null;
  quant_prospect_portfolio_set_id?: string | number | null;
  quant_prospect_portfolio_id?: string | number | null;
  transactions?: RawTransaction[] | null;
}

export function mapTransactionSet(raw: RawTransactionSet): TransactionSetResponse {
  return {
    id: numberOrFallback(raw.id, -1),
    starting_portfolio_id: parseFiniteNumber(raw.quant_portfolio_id),
    target_prospect_portfolio_id: numberOrFallback(raw.quant_prospect_portfolio_id, -1),
    transactions: (raw.transactions ?? []).map((transaction) => ({
      asset_id: parseFiniteNumber(transaction.quant_asset_id),
      asset_name: null,
      isin: null,
      transaction_type: transaction.transaction_type ?? null,
      quantity: parseFiniteNumber(transaction.quantity),
      amount: parseFiniteNumber(transaction.amount),
      amount_sek: parseFiniteNumber(transaction.amount_sek),
      sell_all: transaction.sell_all === true,
    })),
  };
}

export type RiskBand = "LAG" | "MEDELLAG" | "MEDELHOG" | "HOG";

export function bandForVolatility(volatility: number): RiskBand {
  const pct = Math.round(volatility * 100 * 1e9) / 1e9;
  const ceilings = RISK_BAND_CEILINGS_PCT;
  if (pct <= ceilings.low) return "LAG";
  if (pct <= ceilings.midLow) return "MEDELLAG";
  if (pct <= ceilings.midHigh) return "MEDELHOG";
  return "HOG";
}

export function bandForRiskClassification(
  riskClassification: string | null | undefined,
): RiskBand | null {
  switch (riskClassification) {
    case "LOW_RISK":
      return "LAG";
    case "MEDIUM_LOW_RISK":
      return "MEDELLAG";
    case "MEDIUM_HIGH_RISK":
      return "MEDELHOG";
    case "HIGH_RISK":
      return "HOG";
    default:
      return null;
  }
}

export function medianBandPoint(
  frontier: ProspectPortfolio[],
  band: RiskBand,
): ProspectPortfolio | null {
  const inBand = selectBandPoints(frontier, band, frontier.length);
  if (inBand.length === 0) return null;
  return inBand[Math.floor((inBand.length - 1) / 2)];
}

export function selectBandPoints(
  frontier: ProspectPortfolio[],
  band: RiskBand,
  max = 5,
): ProspectPortfolio[] {
  const inBand = frontier
    .filter((point) => bandForVolatility(point.volatility) === band)
    .sort((a, b) => a.volatility - b.volatility || a.id - b.id);
  return evenlySpaced(inBand, max);
}

function evenlySpaced<T>(items: T[], max: number): T[] {
  return spacedIndices(items.length, max).map((index) => items[index]);
}

export function spacedIndices(count: number, max: number): number[] {
  if (max <= 0 || count <= 0) return [];
  if (count <= max) return Array.from({ length: count }, (_, i) => i);
  if (max === 1) return [0];
  const out: number[] = [];
  for (let i = 0; i < max; i++) {
    out.push(Math.round((i * (count - 1)) / (max - 1)));
  }
  return out;
}

function numberOrFallback(value: string | number | null | undefined, fallback = 0): number {
  return parseFiniteNumber(value) ?? fallback;
}
